import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import WorkingFileUpload from '../WorkingFileUpload';
import { 
  X, 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Eye,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import fileUploadService from '../../lib/fileUploadService';
import optimizedFleetService from '../../lib/optimizedFleetService';

/**
 * ImageUploadModal Component
 * Handles image upload and management for individual vehicles
 * 
 * @param {Object} props - Component props
 * @param {Object} props.vehicle - Vehicle data
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close modal callback
 * @param {Function} props.onImageUpdate - Callback when image is updated
 */
const ImageUploadModal = ({ vehicle, isOpen, onClose, onImageUpdate }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);
  const { toast } = useToast();

  // Load current images when modal opens
  useEffect(() => {
    if (isOpen && vehicle) {
      loadCurrentImages();
    }
  }, [isOpen, vehicle]);

  /**
   * Load current images for the vehicle
   */
  const loadCurrentImages = () => {
    const images = [];
    
    // Add main image if exists
    if (vehicle.image_url) {
      images.push({
        id: 'main',
        url: vehicle.image_url,
        type: 'main',
        name: 'Main Image'
      });
    }

    // Add additional images if they exist
    if (vehicle.additional_images && Array.isArray(vehicle.additional_images)) {
      vehicle.additional_images.forEach((img, index) => {
        images.push({
          id: `additional_${index}`,
          url: img,
          type: 'additional',
          name: `Additional Image ${index + 1}`
        });
      });
    }

    setCurrentImages(images);
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (file) => {
    console.log('🔍 ImageUploadModal - handleFileSelect called with:', file);
    console.log('🔍 File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    setSelectedFile(file);
    
    // Create preview URL
    try {
      console.log('🔍 Creating preview URL...');
      const preview = await fileUploadService.fileToBase64(file);
      console.log('✅ Preview URL created:', preview.substring(0, 50) + '...');
      setPreviewUrl(preview);
    } catch (error) {
      console.error('❌ Error creating preview:', error);
      toast({
        title: "Error",
        description: "Failed to create image preview",
        variant: "destructive",
      });
    }
  };

  /**
   * Handle file removal
   */
  const handleFileRemove = () => {
    setSelectedFile(null);
    setPreviewUrl('');
  };

  /**
   * Handle image upload
   */
  const handleUpload = async () => {
    if (!selectedFile || !vehicle) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload and optimize image
      const result = await fileUploadService.uploadAndOptimizeImage(
        selectedFile,
        vehicle.name,
        vehicle.type
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

        if (result.success) {
          console.log('🔍 Upload result:', result);
          console.log('🔍 Optimized path:', result.optimizedPath);
          
          // Add new image to current images list
          const newImage = {
            id: Date.now(), // Simple ID for now
            url: result.optimizedPath,
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type,
            uploadedAt: new Date().toISOString()
          };

        // Update current images state - set new image as main
        setCurrentImages(prev => {
          // Remove old main image and add new one as main
          const filteredImages = prev.filter(img => img.id !== 'main');
          return [
            {
              id: 'main',
              url: newImage.url,
              name: newImage.name,
              isMain: true
            },
            ...filteredImages
          ];
        });

        // Update vehicle with new image as the main image
        const updatedVehicle = {
          ...vehicle,
          image_url: newImage.url
        };

        console.log('🔍 Updating vehicle with data:', updatedVehicle);
        console.log('🔍 Vehicle ID:', vehicle.id);
        console.log('🔍 Vehicle ID type:', typeof vehicle.id);
        
        try {
          const result = await optimizedFleetService.updateVehicle(vehicle.id, updatedVehicle);
          console.log('✅ Vehicle updated successfully:', result);
        } catch (updateError) {
          console.error('❌ Error updating vehicle:', updateError);
          toast({
            title: "Upload saved, but database update failed",
            description: updateError.message || "Image uploaded but vehicle record was not updated",
            variant: "destructive",
          });
          throw updateError;
        }
        
        toast({
          title: "Success",
          description: "Image uploaded and optimized successfully!",
        });

        // Reset form
        setSelectedFile(null);
        setPreviewUrl('');
        setUploadProgress(0);

        // Notify parent component
        onImageUpdate(updatedVehicle);

        // Keep modal open to show the new image
        // onClose(); // Removed this line
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Handle image deletion
   */
  const handleDeleteImage = async (imageId) => {
    try {
      if (imageId === 'main') {
        // Remove main image
        const updatedVehicle = {
          ...vehicle,
          image_url: null
        };
        await optimizedFleetService.updateVehicle(vehicle.id, updatedVehicle);
        
        toast({
          title: "Success",
          description: "Main image removed successfully",
        });
        
        onImageUpdate(updatedVehicle);
        loadCurrentImages();
      } else {
        // Remove additional image
        const imageIndex = parseInt(imageId.split('_')[1]);
        const updatedAdditionalImages = [...(vehicle.additional_images || [])];
        updatedAdditionalImages.splice(imageIndex, 1);
        
        const updatedVehicle = {
          ...vehicle,
          additional_images: updatedAdditionalImages
        };
        
        await optimizedFleetService.updateVehicle(vehicle.id, updatedVehicle);
        
        toast({
          title: "Success",
          description: "Additional image removed successfully",
        });
        
        onImageUpdate(updatedVehicle);
        loadCurrentImages();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
    }
  };

  if (!isOpen || !vehicle) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold">
            Manage Images - {vehicle.name}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isUploading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current Images Section */}
          <div>
            <h3 className="text-lg font-medium mb-4">Current Images</h3>
            {currentImages.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentImages.map((image) => (
                  <div key={image.id} className="relative group">
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => window.open(image.url, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteImage(image.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <p className="text-sm font-medium">{image.name}</p>
                      <p className="text-xs text-gray-500">{image.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No images uploaded yet</p>
              </div>
            )}
          </div>

            {/* Upload New Image Section */}
            <div>
              <h3 className="text-lg font-medium mb-4">Upload New Image</h3>
              
              <WorkingFileUpload
                onFileSelect={handleFileSelect}
                onFileRemove={handleFileRemove}
                maxSize={10}
              />

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Uploading and optimizing...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Upload Button */}
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Image
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Upload Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Upload Tips:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Supported formats: JPG, PNG, WEBP, GIF</li>
                  <li>• Maximum file size: 10MB</li>
                  <li>• Images will be automatically optimized for web</li>
                  <li>• Recommended aspect ratio: 16:9 for best display</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImageUploadModal;
